import React, { useCallback } from 'react';
import qz from 'qz-tray';

import { defaultSAL2030_R01Filter, SAL2030_R01Filter, useSAL2030_R01Store } from 'src/containers/mes/SAL/SAL2030_R01/Store_SAL2030_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { openPdfBlob, parseBlobError } from 'src/utils/pdf/pdfUtil';

import { getSAL2030_R01AllCount, getSAL2030_R01List, downloadExcel_SAL2030_R01, getSAL2030_R01One } from "./Api_SAL2030_R01";
import { SAL2030_R01Dto } from './Dto_SAL2030_R01';
import { AgGridReact } from 'ag-grid-react';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import { downloadExcel } from "src/utils/blob/blobUtil";

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2030_R01Dto> | null>;
	setMessage: (message: string) => void;
}

export const Content_SAL2030_R01 = (props: Props) => {

	const {
		gridRef,
		setMessage,
	} = props;

	const {
		SAL2030_R01Filter, setSAL2030_R01Filter,
		SAL2030_R01PastFilter, setSAL2030_R01PastFilter,
		SAL2030_R01List, setSAL2030_R01List,
		setSelectedSAL2030_R01, initSelectedSAL2030_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
		pdfBlob,
		setPdf,
	} = useSAL2030_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	useFetchActionbarAuth("SAL2030_R01");

	const getParams = (filter: SAL2030_R01Filter) => {
		const params: Partial<SAL2030_R01Filter> = {
			compCd: filter.compCd || undefined,
			dateRange: filter.dateRange || undefined,
			customCd: filter.customCd || undefined,
			itemCd: filter.itemCd || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getSAL2030_R01AllCount(getParams(SAL2030_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [SAL2030_R01Filter, setAllCount]);

	const fetchOne = useCallback(async (row?: SAL2030_R01Dto) => {
		const target = row ?? useSAL2030_R01Store.getState().SAL2030_R01List[0];
		if (!target) {
			setPdf(null);
			return;
		}
		try {
			const response = await getSAL2030_R01One(target);
			const blob = new Blob([response.data], { type: 'application/pdf' });
			setPdf(blob);
		} catch (error) {
			console.error('PDF 조회 실패:', error);
			setPdf(null);
			showWarning('PDF를 불러오지 못했습니다.');
		}
	}, [setPdf]);

	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setLoadingSpinner(true);

		const responseData = (await getSAL2030_R01List(
			'search',
			getParams(SAL2030_R01Filter),
			SAL2030_R01List.length ?? 0
		));

		let newList = [...responseData.data.data ] as SAL2030_R01Dto[];
		setSAL2030_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				() => {},
				() => true,
			);
			setSAL2030_R01PastFilter(SAL2030_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
			const firstRow = useSAL2030_R01Store.getState().SAL2030_R01List[0];
			setSelectedSAL2030_R01(firstRow);
			await fetchOne(firstRow);
		} else {
			showWarning('데이터가 없습니다.');
			setSAL2030_R01List([]);
			initSelectedSAL2030_R01();
			setPdf(null);
		}
		setLoadingSpinner(false);
		return responseData;
	}, [SAL2030_R01Filter, SAL2030_R01List, setSAL2030_R01List, setSAL2030_R01PastFilter, setSelectedSAL2030_R01, initSelectedSAL2030_R01, setLoadingSpinner, setMessage, gridRef, renewalAllCount, setScrollTop, fetchOne, setPdf]);

	const handleExcelClick = useCallback(async () => {
		if (SAL2030_R01List.length === 0) {
			showWarning("엑셀 다운로드할 데이터가 없습니다.");
			return;
		}
		
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_SAL2030_R01('search', getParams(SAL2030_R01Filter));
			
			// Base64 문자열 → Blob 변환 (SAL1020_R01, SAL1030_R01, SAL2080_R01 패턴)
			const binaryString = atob(response.data.data);
			const byteArray = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				byteArray[i] = binaryString.charCodeAt(i);
			}
			const blob = new Blob([byteArray], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});
			
			downloadExcel(blob, '기간별 매출현황 조회.xlsx');
			
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('엑셀 다운로드 실패:', error);
			showError('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [SAL2030_R01List, SAL2030_R01Filter, setLoadingSpinner]);

	const handlePrintClick = useCallback(async () => {
		if (!pdfBlob) {
			showWarning('출력할 PDF가 없습니다.');
			return;
		}

		setLoadingSpinner(true);
		try {
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(pdfBlob, 'report1');
				if (ok) {
					showSuccess('PDF가 출력되었습니다.');
				} else {
					openPdfBlob(pdfBlob);
				}
			} else {
				openPdfBlob(pdfBlob);
				showSuccess('PDF가 출력되었습니다.');
			}
		} catch (error) {
			console.error('PDF 출력 실패:', error);
			showWarning(await parseBlobError(error));
		} finally {
			setLoadingSpinner(false);
		}
	}, [pdfBlob, printPdfFromBlob, setLoadingSpinner]);

	return {
		fetchData,
		fetchOne,
		handleExcelClick,
		handlePrintClick,
	};
}

